import { create } from '@bufbuild/protobuf'
import { UuidDto, UuidDtoSchema } from '@/generated/Messages/Common_pb'
import { assertNotNullish } from './assertNotNullish'

/**
 * Takes a `UuidDto` and transforms it into it's string representation.
 * Allows passing of `undefined`, so a disable eslint comment isn't necessary.
 * @param u The dto to be transformed.
 * @returns the string representation of the `UuidDto`.
 * @throws An `Error` if `undefined` has been passed.
 */
export const stringifyId = ({ high64, low64 }: Partial<UuidDto> = {}) => {
    assertNotNullish(high64)
    assertNotNullish(low64)

    return `${(high64 & 0xffffffffn).toString(16).padStart(8, '0')}-${(
        (high64 & 0xffff00000000n) >>
        32n
    )
        .toString(16)
        .padStart(4, '0')}-${((high64 & 0xffff000000000000n) >> 48n)
        .toString(16)
        .padStart(4, '0')}-${(
        ((low64 & 0xffn) << 8n) |
        ((low64 & 0xff00n) >> 8n)
    )
        .toString(16)
        .padStart(4, '0')}-${(
        ((low64 & 0xff0000n) << 24n) |
        ((low64 & 0xff000000n) << 8n) |
        ((low64 & 0xff00000000n) >> 8n) |
        ((low64 & 0xff0000000000n) >> 24n) |
        ((low64 & 0xff000000000000n) >> 40n) |
        ((low64 & 0xff00000000000000n) >> 56n)
    )
        .toString(16)
        .padStart(12, '0')}`
}

/**
 * Transforms the passed string into a `UuidDto`.
 * @param u The string represenation of a uuid.
 * @returns the `UuidDto`.
 */
export const parseId = (u?: string) => {
    assertNotNullish(u)

    const low64Parts = [
        u.substring(34),
        u.substring(32, 34),
        u.substring(30, 32),
        u.substring(28, 30),
        u.substring(26, 28),
        u.substring(24, 26),
        u.substring(21, 23),
        u.substring(19, 21),
    ]
    const high64Parts = [
        u.substring(14, 18),
        u.substring(9, 13),
        u.substring(0, 8),
    ]

    const low64 = BigInt(`0x${low64Parts.join('')}`)
    const high64 = BigInt(`0x${high64Parts.join('')}`)

    assertNotNullish(high64, 'High 64 is undefined')
    assertNotNullish(low64, 'Low 64 is undefined')

    return create(UuidDtoSchema, { high64, low64 })
}
